import type { World } from "./types.js";
import { createWorld } from "./types.js";
import { RippleSystem } from "./ripple.js";
import { WingSystem, type WingStats } from "./wing.js";
import { Clock } from "./clock.js";

export interface EngineOptions {
  rng?: () => number;
  reducedMotion?: boolean;
  /** Runs once a frame, after the simulation step. It's the host's render hook. It stays out
      of `tick()`, so tests can step the simulation without painting anything. */
  onFrame?: () => void;
}

export class Engine {
  readonly world: World;
  private readonly ripple: RippleSystem;
  private readonly wing: WingSystem;
  private readonly clock: Clock;
  private readonly onFrame: (() => void) | undefined;

  constructor(opts: EngineOptions = {}) {
    const rng = opts.rng ?? Math.random;
    this.onFrame = opts.onFrame;
    this.world = createWorld({ reducedMotion: opts.reducedMotion ?? false });
    this.ripple = new RippleSystem(undefined, rng);
    // A wing beat sends its wave through the ripple system, which decides if it can start.
    // The wing rate-limits click flaps (`WING_CLICK_RATE`); ambient flaps keep their own
    // schedule.
    this.wing = new WingSystem(
      undefined,
      rng,
      (now, amp, strength, source, level) => {
        this.ripple.emit(this.world, now, amp, strength, source, level);
      },
      () => this.ripple.canEmit(this.world),
    );
    this.clock = new Clock((time, dt) => {
      this.tick(time, dt);
      this.onFrame?.();
    });
  }

  /** One simulation step, at `now` seconds since the start. */
  tick(now: number, dt: number): void {
    this.world.time = now;
    this.ripple.update(this.world, dt); // ends finished waves, every frame
    if (!this.world.reducedMotion) this.wing.update(this.world); // the beats (may send a wave)
  }

  /** A flap at an explicit effect `level`, with an optional strength (for the page and tests). */
  flap(
    now: number = this.world.time,
    level = 3,
    strength?: number,
    speed = 1,
    source: "click" | "theme" = "click",
  ): void {
    this.wing.flap(now, level, strength, speed, source);
  }

  visualFlap(now: number = this.world.time, strength = 1): void {
    if (this.world.reducedMotion || this.wing.stats().beatOn) return;
    this.wing.visualFlap(now, strength);
  }

  /** Drops every live wave. */
  calm(): void {
    this.world.ripples.items.length = 0;
  }

  /** What the wing is doing right now. */
  wingStats(): WingStats {
    return this.wing.stats();
  }

  start(): void {
    this.clock.start();
  }
  stop(): void {
    this.clock.stop();
  }
}
